import { useEffect, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { GroupTypes } from "../../../abstract/controller/enum/GroupTypes";
import { ItemTypes } from "../../../abstract/controller/enum/dnd/ItemTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import * as pathUtils from "../../../abstract/controller/utils/pathUtils";
import { setWindowLock } from "../../../abstract/redux/slice/overlaySlice";
import {
  getStatusPositioned,
  setDrag,
  setIdle,
  setPosition,
} from "../../../abstract/redux/slice/statusSlice";
import {
  getNodeStructure,
  handleChoiceMove,
  handleParameterMove,
} from "../../../abstract/redux/slice/structureSlice";
import DialogSelect from "../../ui/modal/DialogSelect";
import { DragOverlay } from "../../ui/overlay/DragOverlay";
import { showChoiceDrop } from "../choice/logic/showChoiceDrop";
import styles from "./UndefinedPanel.module.scss";
import { showParameterDrop } from "./logic/showParameterDrop";

function UndefinedPanel({ id }) {
  const _dispatch = useDispatch();
  const _isNodePositioned = useSelector(getStatusPositioned);

  const _node = useSelector(getNodeStructure(id));
  const _nodeParent = useSelector(getNodeStructure(pathUtils.getIdParent(id)));

  const [_showModal, _setShowModal] = useState(false);

  const _nodeTarget = useRef(null);

  const _isGlobalTop = nodeUtils.isParameterTopGlobal(_node);
  const _isGlobalNested = nodeUtils.isParameterNestedGlobal(_node);

  //---------------------------------------------------------------------

  const [{ _nodeDrag, _type, _isOver, _canDrop }, drop] = useDrop(
    () => ({
      accept: [ItemTypes.CHOICE, ItemTypes.PARAMETER],
      collect: (monitor) => ({
        _nodeDrag: monitor.getItem()?.element,
        _type: monitor.getItemType(),
        _isOver: monitor.isOver(),
        _canDrop: monitor.canDrop(),
      }),
      canDrop(item) {
        if (nodeUtils.isParameterGlobal(_node) && !nodeUtils.isParameterGlobal(item.element)) {
          return false;
        }

        if (_type === ItemTypes.CHOICE) {
          return showChoiceDrop(item.element, _node, _nodeParent, true);
        } else {
          return showParameterDrop(item.element, _node, _nodeParent, true);
        }
      },
      drop(item, monitor) {
        _dispatch(setIdle());

        if (monitor.getItemType() === ItemTypes.CHOICE) {
          _dispatch(handleChoiceMove(nodeUtils.getID(item.element), id, true));
          return;
        } else {
          // Restrictions for global parameters.
          if (_isGlobalTop || _isGlobalNested) {
            // If the dragged parameter has references, it can be only linked (not moved).
            if (nodeUtils.getIdReferences(item.element) > 0) {
              _dispatch(handleParameterMove(nodeUtils.getID(item.element), id, true, true));
              // If the dragged parameter is linked, it can be only moved (not linked).
            } else if (nodeUtils.getIdTarget(item.element)) {
              _dispatch(handleParameterMove(nodeUtils.getID(item.element), id, true, false));
              // If the dragged parameter has no references and is not linked, it can be linked or moved.
            } else {
              _nodeTarget.current = item.element;
              _dispatch(setWindowLock(true));
              _setShowModal(true);
            }
            return;
          }

          // Function parameters have no additional restrictions.
          _dispatch(handleParameterMove(nodeUtils.getID(item.element), id, true, false));
        }
      },
    }),
    [id, _node, _nodeParent]
  );

  const _isNodeOver = _isNodePositioned && _isOver;

  useEffect(() => {
    // if (!_canDrop) {
    //   return;
    // }

    if (_isOver) {
      const group = pathUtils.determineGroup(nodeUtils.getID(_nodeDrag), id);

      if (group === GroupTypes.INTERNAL_FUNCTIONS || group === GroupTypes.INTERNAL_GLOBALS) {
        // if (_type === ItemTypes.CHOICE) {
        _dispatch(setPosition());
        // }
      } else {
        _dispatch(setPosition({ external: true }));
      }
    } else {
      _dispatch(setDrag({ fromPosition: true }));
    }
  }, [id, _type, _nodeDrag, _isOver, _canDrop, _dispatch]);

  //---------------------------------------------------------------------

  const getClass = () => {
    return `${styles["panel"]} ${_isNodeOver ? styles["panel-extended"] : ""}`;
  };

  const actionMove = () => {
    return {
      text: "move",
      handler: () => {
        _dispatch(handleParameterMove(nodeUtils.getID(_nodeTarget.current), id, true, false));
        _dispatch(setWindowLock(false));
        _setShowModal(false);
      },
    };
  };

  const actionCancel = () => {
    return {
      text: "cancel",
      handler: () => {
        _dispatch(setWindowLock(false));
        _setShowModal(false);
      },
    };
  };

  const actionLink = () => {
    return {
      text: "link",
      handler: () => {
        _dispatch(handleParameterMove(nodeUtils.getID(_nodeTarget.current), id, true, true));
        _dispatch(setWindowLock(false));
        _setShowModal(false);
      },
    };
  };

  const hasReferences = nodeUtils.isParameterReference(_nodeTarget.current);

  const actions = [];

  actions.push(actionLink());

  if (!hasReferences) {
    actions.push(actionMove());
  }

  actions.push(actionCancel());

  return (
    <>
      {_showModal && (
        <DialogSelect
          textHeader="parameter options"
          textBody="Select one of the available actions."
          actions={actions}
          cancel={() => {
            _setShowModal(false);
          }}
        />
      )}
      <div ref={_canDrop ? drop : null} className={getClass()}>
        <DragOverlay type={_type} isVisible={_isNodeOver} isEmpty />
      </div>
    </>
  );
}

export default UndefinedPanel;
